import { AbstractControl, ValidationErrors } from "@angular/forms";

/**
 * Field-level counterpart to checkIfEndDateAfterStartDate, split into two
 * independent validators so the "sluttid before starttid" error is
 * attributed to the field that is actually wrong:
 *
 * - If endDate > startDate, the range is valid regardless of endTime.
 * - If endDate === startDate, endTime must be after startTime.
 * - If endDate < startDate, the range is invalid regardless of endTime -
 *   only the date is wrong.
 *
 * Each validator only reads startDate/startTime's .invalid flag (safe -
 * those controls carry independent, non-circular validators). They never
 * read endDate's or endTime's own .invalid flag, so there is no circular
 * dependency between the two end controls.
 */

function toDateOnly(value: string): Date {
    return new Date(value);
}

export function endDateAfterStartDateValidator(control: AbstractControl): ValidationErrors | null {
    const group = control.parent;
    if (!group) return null;

    const startDate: string = group.get('startDate')?.invalid ? null : group.get('startDate')?.value;
    const endDate: string = control.value;

    if (!startDate || !endDate) return null;

    const isValid = toDateOnly(endDate) >= toDateOnly(startDate);
    return isValid ? null : { invalidTimeOrder: true };
};

export function endTimeAfterStartTimeValidator(control: AbstractControl): ValidationErrors | null {
    const group = control.parent;
    if (!group) return null;

    const startDate: string = group.get('startDate')?.invalid ? null : group.get('startDate')?.value;
    const startTime: string = group.get('startTime')?.invalid ? null : group.get('startTime')?.value;
    const endDate: string = group.get('endDate')?.value;
    const endTime: string = control.value;

    if (!startDate || !startTime || !endDate || !endTime) return null;

    // Different dates: the date itself is what's right/wrong, endTime is irrelevant.
    if (toDateOnly(endDate).getTime() !== toDateOnly(startDate).getTime()) return null;

    const startDateTime = new Date(startDate + ' ' + startTime);
    const endDateTime = new Date(endDate + ' ' + endTime);

    const isValid = startDateTime < endDateTime;
    return isValid ? null : { invalidTimeOrder: true };
};
